import React, { useState, useEffect } from 'react'
import styles from './StudentArchive.module.scss'
import { 
  getStudentCases, 
  registerStudent, 
  createStudentCase,
  createStudentDocument,
  uploadStudentDocumentFile,
  updateStudentCase,
  deleteStudentCase
} from '../../../service/ArchiveService'
import { ScaleLoader } from 'react-spinners'
import Notification from '../../../utils/Notifications'

function StudentArchive() {
  const [cases, setCases] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCase, setSelectedCase] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showRegisterForm, setShowRegisterForm] = useState(false)
  const [showDocumentForm, setShowDocumentForm] = useState(false)
  const [registerData, setRegisterData] = useState({
    last_name: '',
    first_name: '',
    middle_name: '',
    date_of_birth: '',
    gender: 'M',
    student_id: '',
    course: 1,
    admission_year: new Date().getFullYear(),
    email: '',
    phone_number: '',
    address: '',
  })
  const [documentData, setDocumentData] = useState({
    student_case: null,
    document_type: 'admission_order',
    title: '',
    document_date: '',
    pages: 0,
    confidentiality_level: 'internal',
    notes: ''
  })
  const [uploadFile, setUploadFile] = useState(null)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '', sound: '' })

  useEffect(() => {
    loadStudentCases()
  }, [])

  const loadStudentCases = async () => {
    try {
      setLoading(true)
      const data = await getStudentCases()
      setCases(data)
    } catch (error) {
      console.error('Ошибка при загрузке личных дел студентов:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при загрузке данных',
        type: 'error',
        sound: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRegisterStudent = async (e) => {
    e.preventDefault()
    try {
      // Подготавливаем данные: заменяем пустые строки на null для необязательных полей
      const preparedData = {
        ...registerData,
        email: registerData.email || null,
        phone_number: registerData.phone_number || null,
        address: registerData.address || null,
        middle_name: registerData.middle_name || null,
        date_of_birth: registerData.date_of_birth || null,
      }
      const newCase = await registerStudent(preparedData)
      setCases([...cases, newCase])
      setRegisterData({
        last_name: '',
        first_name: '',
        middle_name: '',
        date_of_birth: '',
        gender: 'M',
        student_id: '',
        course: 1,
        admission_year: new Date().getFullYear(),
        email: '',
        phone_number: '',
        address: '',
      })
      setShowRegisterForm(false)
      setNotify({
        isOpen: true,
        message: 'Студент успешно зарегистрирован!',
        type: 'success',
        sound: 'success',
      })
    } catch (error) {
      console.error('Ошибка при регистрации студента:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при регистрации студента',
        type: 'error',
        sound: 'error',
      })
    }
  }

  const handleAddDocument = async (e) => {
    e.preventDefault()
    try {
      const docData = {
        ...documentData,
        student_case: selectedCase.id
      }
      // Создаем документ с файлом сразу
      const newDocument = await createStudentDocument(docData, uploadFile)
      
      // Обновляем выбранное дело
      const updatedCases = cases.map(c => 
        c.id === selectedCase.id 
          ? { ...c, documents: [...(c.documents || []), newDocument] }
          : c
      )
      setCases(updatedCases)
      setSelectedCase({ ...selectedCase, documents: [...(selectedCase.documents || []), newDocument] })
      
      setDocumentData({
        student_case: null,
        document_type: 'admission_order',
        title: '',
        document_date: '',
        pages: 0,
        confidentiality_level: 'internal',
        notes: ''
      })
      setUploadFile(null)
      setShowDocumentForm(false)
      setNotify({
        isOpen: true,
        message: 'Документ успешно добавлен!',
        type: 'success',
        sound: 'success',
      })
    } catch (error) {
      console.error('Ошибка при добавлении документа:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при добавлении документа',
        type: 'error',
        sound: 'error',
      })
    }
  }

  const handleUpdateCase = async (caseId, updates) => {
    try {
      const updatedCase = await updateStudentCase(caseId, updates)
      setCases(cases.map(c => c.id === caseId ? updatedCase : c))
      if (selectedCase?.id === caseId) {
        setSelectedCase(updatedCase)
      }
      setNotify({
        isOpen: true,
        message: 'Дело успешно обновлено!',
        type: 'success',
        sound: 'success',
      })
    } catch (error) {
      console.error('Ошибка при обновлении дела:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при обновлении дела',
        type: 'error',
        sound: 'error',
      })
    }
  }

  const handleDeleteCase = async (caseId) => {
    if (!window.confirm('Вы уверены, что хотите удалить это дело?')) {
      return
    }
    try {
      await deleteStudentCase(caseId)
      setCases(cases.filter(c => c.id !== caseId))
      if (selectedCase?.id === caseId) {
        setSelectedCase(null)
      }
      setNotify({
        isOpen: true,
        message: 'Дело успешно удалено!',
        type: 'success',
        sound: 'success',
      })
    } catch (error) {
      console.error('Ошибка при удалении дела:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при удалении дела',
        type: 'error',
        sound: 'error',
      })
    }
  }

  const filteredCases = cases.filter(
    (caseItem) =>
      caseItem.student_full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      caseItem.student_id?.includes(searchQuery) ||
      caseItem.faculty?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const documentTypes = [
    { value: 'admission_order', label: 'Приказ о зачислении' },
    { value: 'grade_sheet', label: 'Ведомость' },
    { value: 'diploma', label: 'Диплом' },
    { value: 'academic_certificate', label: 'Академическая справка' },
    { value: 'transcript', label: 'Выписка оценок' },
    { value: 'medical_certificate', label: 'Медицинская справка' },
    { value: 'reference_letter', label: 'Рекомендательное письмо' },
    { value: 'other', label: 'Прочее' },
  ]

  const getDocumentTypeLabel = (value) => {
    const type = documentTypes.find(t => t.value === value)
    return type ? type.label : value
  }

  const getConfidentialityLabel = (value) => {
    const levels = {
      'public': 'Открытый',
      'internal': 'Внутренний',
      'confidential': 'Конфиденциальный',
      'secret': 'Секретный'
    }
    return levels[value] || value
  }

  if (loading) {
    return (
      <div className={styles.loading_container}>
        <ScaleLoader color="#00aa44" />
      </div>
    )
  }

  return (
    <>
      <Notification notify={notify} setNotify={setNotify} />
      <div className={styles.student_archive}>
      <div className={styles.header}>
        <h1>Архив студентов</h1>
        <button
          className={styles.btn_register}
          onClick={() => setShowRegisterForm(!showRegisterForm)}
        >
          + Зарегистрировать студента
        </button>
      </div>

      {showRegisterForm && (
        <div className={styles.modal}>
          <div className={styles.modal_content}>
            <button 
              className={styles.close_btn}
              onClick={() => setShowRegisterForm(false)}
              type="button"
            >
              ×
            </button>
            <h2>Регистрация студента</h2>
            <form onSubmit={handleRegisterStudent}>
              <div className={styles.form_row}>
                <div className={styles.form_field}>
                  <label>Фамилия *</label>
                  <input
                    type="text"
                    placeholder="Введите фамилию"
                    required
                    value={registerData.last_name}
                    onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
                  />
                </div>
                <div className={styles.form_field}>
                  <label>Имя *</label>
                  <input
                    type="text"
                    placeholder="Введите имя"
                    required
                    value={registerData.first_name}
                    onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
                  />
                </div>
              </div>
              <div className={styles.form_field}>
                <label>Отчество</label>
                <input
                  type="text"
                  placeholder="Введите отчество"
                  value={registerData.middle_name}
                  onChange={(e) => setRegisterData({ ...registerData, middle_name: e.target.value })}
                />
              </div>
              <div className={styles.form_row}>
                <div className={styles.form_field}>
                  <label>Дата рождения</label>
                  <input
                    type="date"
                    value={registerData.date_of_birth}
                    onChange={(e) => setRegisterData({ ...registerData, date_of_birth: e.target.value })}
                  />
                </div>
                <div className={styles.form_field}>
                  <label>Пол</label>
                  <select
                    value={registerData.gender}
                    onChange={(e) => setRegisterData({ ...registerData, gender: e.target.value })}
                  >
                    <option value="M">Мужской</option>
                    <option value="F">Женский</option>
                  </select>
                </div>
              </div>
              <div className={styles.form_row}>
                <div className={styles.form_field}>
                  <label>Студенческий билет *</label>
                  <input
                    type="text"
                    placeholder="Номер студенческого билета"
                    required
                    value={registerData.student_id}
                    onChange={(e) => setRegisterData({ ...registerData, student_id: e.target.value })}
                  />
                </div>
                <div className={styles.form_field}>
                  <label>Курс</label>
                  <input
                    type="number"
                    placeholder="1-6"
                    min="1"
                    max="6"
                    value={registerData.course}
                    onChange={(e) => setRegisterData({ ...registerData, course: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              <div className={styles.form_field}>
                <label>Год поступления</label>
                <input
                  type="number"
                  placeholder="Например, 2024"
                  value={registerData.admission_year}
                  onChange={(e) => setRegisterData({ ...registerData, admission_year: parseInt(e.target.value) })}
                />
              </div>
              <div className={styles.form_field}>
                <label>Email</label>
                <input
                  type="email"
                  placeholder="email@example.com"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                />
              </div>
              <div className={styles.form_field}>
                <label>Телефон</label>
                <input
                  type="tel"
                  placeholder="+996 XXX XXX XXX"
                  value={registerData.phone_number}
                  onChange={(e) => setRegisterData({ ...registerData, phone_number: e.target.value })}
                />
              </div>
              <div className={styles.form_field}>
                <label>Адрес проживания</label>
                <textarea
                  placeholder="Укажите полный адрес"
                  value={registerData.address}
                  onChange={(e) => setRegisterData({ ...registerData, address: e.target.value })}
                />
              </div>
              <div className={styles.form_actions}>
                <button type="submit" className={styles.btn_save}>
                  Зарегистрировать
                </button>
                <button
                  type="button"
                  className={styles.btn_cancel}
                  onClick={() => setShowRegisterForm(false)}
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className={styles.search_bar}>
        <input
          type="text"
          placeholder="Поиск по ФИО, номеру студента, факультету..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.search_input}
        />
      </div>

      <div className={styles.content}>
        <div className={styles.cases_container}>
          <h2>Личные дела студентов ({filteredCases.length})</h2>
          <div className={styles.cases_grid}>
            {filteredCases.map((caseItem) => (
              <div
                key={caseItem.id}
                className={`${styles.case_card} ${selectedCase?.id === caseItem.id ? styles.active : ''}`}
                onClick={() => setSelectedCase(caseItem)}
              >
                <div className={styles.card_header}>
                  <div className={styles.student_name}>{caseItem.student_full_name}</div>
                  <div className={styles.status_badge}>{caseItem.status}</div>
                </div>
                <div className={styles.card_body}>
                  <p>
                    <strong>ФИО:</strong> {caseItem.student_full_name}
                  </p>
                  <p>
                    <strong>№ Студента:</strong> {caseItem.student_id}
                  </p>
                  <p>
                    <strong>Факультет:</strong> {caseItem.faculty}
                  </p>
                  <p>
                    <strong>№ Дела:</strong> {caseItem.case_number}
                  </p>
                  {caseItem.is_digitized && <span className={styles.digitized_badge}>✓ Оцифровано</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedCase && (
          <div className={styles.details_panel}>
            <div className={styles.panel_header}>
              <h2>Документы в деле</h2>
              <button
                className={styles.btn_add_doc}
                onClick={() => setShowDocumentForm(!showDocumentForm)}
              >
                + Добавить документ
              </button>
            </div>

            {showDocumentForm && (
              <div className={styles.document_form}>
                <h3>Добавление документа</h3>
                <form onSubmit={handleAddDocument}>
                  <div className={styles.form_field}>
                    <label>Тип документа *</label>
                    <select
                      value={documentData.document_type}
                      onChange={(e) => setDocumentData({ ...documentData, document_type: e.target.value })}
                      required
                    >
                      {documentTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.form_field}>
                    <label>Название документа *</label>
                    <input
                      type="text"
                      placeholder="Введите название"
                      required
                      value={documentData.title}
                      onChange={(e) => setDocumentData({ ...documentData, title: e.target.value })}
                    />
                  </div>
                  <div className={styles.form_field}>
                    <label>Дата документа *</label>
                    <input
                      type="date"
                      required
                      value={documentData.document_date}
                      onChange={(e) => setDocumentData({ ...documentData, document_date: e.target.value })}
                    />
                  </div>
                  <div className={styles.form_field}>
                    <label>Количество страниц</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={documentData.pages}
                      onChange={(e) => setDocumentData({ ...documentData, pages: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className={styles.form_field}>
                    <label>Примечания</label>
                    <textarea
                      placeholder="Дополнительная информация"
                      value={documentData.notes}
                      onChange={(e) => setDocumentData({ ...documentData, notes: e.target.value })}
                    />
                  </div>
                  <div className={styles.form_field}>
                    <label>Файл документа</label>
                    <input
                      type="file"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                    />
                  </div>
                  <div className={styles.form_actions}>
                    <button type="submit" className={styles.btn_save}>
                      Добавить
                    </button>
                    <button
                      type="button"
                      className={styles.btn_cancel}
                      onClick={() => setShowDocumentForm(false)}
                    >
                      Отмена
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className={styles.case_info}>
              <p>
                <strong>Студент:</strong> {selectedCase.student_full_name}
              </p>
              <p>
                <strong>Дело:</strong> {selectedCase.case_number}
              </p>
              <p>
                <strong>Срок хранения:</strong> {selectedCase.storage_term} лет
              </p>
              <div className={styles.case_actions}>
                <button
                  className={styles.btn_digitize}
                  onClick={() => handleUpdateCase(selectedCase.id, { is_digitized: !selectedCase.is_digitized })}
                >
                  {selectedCase.is_digitized ? '✓ Оцифровано' : 'Оцифровать'}
                </button>
                <button
                  className={styles.btn_delete}
                  onClick={() => handleDeleteCase(selectedCase.id)}
                >
                  Удалить дело
                </button>
              </div>
            </div>

            <div className={styles.documents}>
              {selectedCase.documents && selectedCase.documents.length > 0 ? (
                <div className={styles.documents_list}>
                  {selectedCase.documents.map((doc) => (
                    <div key={doc.id} className={styles.document_item}>
                      <div className={styles.doc_type}>{getDocumentTypeLabel(doc.document_type)}</div>
                      <div className={styles.doc_info}>
                        <p className={styles.doc_title}>{doc.title}</p>
                        <small>Дата: {new Date(doc.document_date).toLocaleDateString('ru-RU')}</small>
                        {doc.pages > 0 && <small> • Страниц: {doc.pages}</small>}
                        {doc.files?.length > 0 && (
                          <div className={styles.doc_files}>
                            {doc.files.map((file) => (
                              <div key={file.id} className={styles.file_item}>
                                <a href={file.file} download className={styles.file_link}>
                                  📄 {file.file_name}
                                </a>
                                <a 
                                  href={file.file} 
                                  download={file.file_name}
                                  className={styles.btn_download}
                                  title="Скачать файл"
                                >
                                  ⬇️ Скачать
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.no_documents}>Документов нет</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  )
}

export default StudentArchive
